/**
 * aiConfig — shared, presentational configuration data for the AI control
 * surfaces (the header status strip and the AI Cockpit page).
 *
 * These were previously defined inside AIControlBar.jsx and imported *from the
 * header* by the Cockpit, coupling the two. They now live here so both surfaces
 * depend on a neutral module instead of each other. Pure data + no React.
 */

/**
 * deriveAiNowText — the ONE plain-language "what is the AI doing right now?"
 * string. Both the header status strip (AIControlBar) and the cockpit's
 * Now/Next panel call this so they can never contradict each other at a glance.
 *
 * @param {object}  o
 * @param {string}  o.mode            spectrum posture id
 * @param {boolean} [o.paused]        autonomy kill-switch engaged
 * @param {number}  [o.advisorCount]  pending advisor proposals
 * @param {string}  [o.advisorTopTitle] title of the top pending proposal
 * @returns {string}
 */
export function deriveAiNowText({ mode, paused = false, advisorCount = 0, advisorTopTitle = null } = {}) {
    if (mode === 'dormant') return 'Asleep';
    if (paused) return 'Paused — holding all actions';
    // Strings contrast the CONTROL MODEL of each posture, not just the activity:
    // observing forms intentions but never runs them; advising needs approval;
    // acting runs on its own.
    if (mode === 'observing') return 'Watching only — forms intentions, never acts';
    if (mode === 'advising') {
        if (advisorCount > 0) return advisorTopTitle || `${advisorCount} awaiting approval`;
        return 'Proposing — nothing runs until you approve';
    }
    if (mode === 'acting') return 'Acting on its own — no approval needed';
    return 'Asleep';
}

/**
 * Friendly labels for the raw advisor `capability` id (the subsystem that
 * raised a proposal). Falls back to a title-cased version of the id.
 */
const CAPABILITY_LABELS = {
    control: 'Enforcement',
    situation: 'Situation',
    games: 'Games & Challenges',
    policy: 'Policy',
    economy: 'Economy',
    journal: 'Journal',
    dossier: 'Profile',
    content: 'Content',
    browser: 'Browser',
    schedule: 'Schedule',
};

export function capabilityLabel(id) {
    if (!id) return 'AI';
    return CAPABILITY_LABELS[id] || String(id).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Human-facing labels for proposal severities (color-plus-text, not color-only). */
export const SEVERITY_LABELS = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    info: 'Info',
};

/**
 * describeAction — a compact, read-only "what this will do" line derived from a
 * proposal's `action` key + `params`. Templates map an executor/action key to a
 * human verb; unknown actions fall back to a generic "Run <action>" with the
 * params appended so the effect is never fully hidden.
 */
const ACTION_TEMPLATES = {
    lock_screen: (p) => `Locks the screen${p?.durationMinutes ? ` for ${p.durationMinutes} minute${p.durationMinutes === 1 ? '' : 's'}` : ''}`,
    screen_lockout: (p) => `Locks the screen${p?.durationMinutes ? ` for ${p.durationMinutes} minute${p.durationMinutes === 1 ? '' : 's'}` : ''}`,
    block_app: (p) => `Blocks ${p?.app || p?.appName || p?.target || 'an app'}${p?.durationMinutes ? ` for ${p.durationMinutes}m` : ''}`,
    block_website: (p) => `Blocks ${p?.domain || p?.url || p?.site || 'a website'}${p?.durationMinutes ? ` for ${p.durationMinutes}m` : ''}`,
    close_app: (p) => `Force-closes ${p?.app || p?.appName || p?.target || 'an app'}`,
    close_tab: (p) => `Closes ${p?.domain || p?.url || 'a browser tab'}`,
    shock: (p) => `Triggers the shock collar${p?.intensity != null ? ` at ${p.intensity}%` : ''}`,
    shock_collar: (p) => `Triggers the shock collar${p?.intensity != null ? ` at ${p.intensity}%` : ''}`,
    write_lines: (p) => `Assigns writing lines${p?.count ? ` (×${p.count})` : ''}${p?.text ? `: "${p.text}"` : ''}`,
    journal_prompt: (p) => `Assigns a journal prompt${p?.prompt ? `: "${p.prompt}"` : ''}`,
    spin_wheel: () => 'Spins the consequence wheel',
    start_challenge: (p) => `Starts a challenge${p?.challenge || p?.type ? `: ${p.challenge || p.type}` : ''}`,
    launch_game: (p) => `Launches a game${p?.game || p?.type ? `: ${p.game || p.type}` : ''}`,
    forced_break: (p) => `Forces a screen break${p?.durationMinutes ? ` for ${p.durationMinutes}m` : ''}`,
    adjust_economy: (p) => `Adjusts the balance${p?.amount != null ? ` by ${p.amount > 0 ? '+' : ''}${p.amount}` : ''}`,
    fine: (p) => `Fines the subject${p?.amount != null ? ` ${p.amount}` : ''}`,
    reward: (p) => `Grants a reward${p?.amount != null ? ` of ${p.amount}` : ''}`,
    set_wallpaper: () => 'Changes the desktop wallpaper',
    play_sound: (p) => `Plays a sound cue${p?.sound ? `: ${p.sound}` : ''}`,
    send_message: () => 'Sends a chat message to the subject',
    dossier_entry: () => 'Records a dossier observation',
    input_sabotage: (p) => `Sabotages input${p?.durationMinutes ? ` for ${p.durationMinutes}m` : ''}`,
};

export function describeAction(proposal) {
    if (!proposal) return null;
    const action = proposal.action || proposal.executor || proposal.tool;
    const params = proposal.params || proposal.args || {};
    if (!action) return null;
    const tmpl = ACTION_TEMPLATES[action];
    if (tmpl) {
        try { return tmpl(params); } catch { /* fall through */ }
    }
    // Generic fallback — never hide the effect entirely.
    const keys = params && typeof params === 'object' ? Object.keys(params) : [];
    const detail = keys.length
        ? ' (' + keys.map((k) => `${k}: ${formatParamValue(params[k])}`).join(', ') + ')'
        : '';
    const verb = String(action).replace(/[_-]+/g, ' ');
    return `Runs ${verb}${detail}`;
}

function formatParamValue(v) {
    if (v == null) return '—';
    if (typeof v === 'object') { try { return JSON.stringify(v); } catch { return String(v); } }
    return String(v);
}

// Emotional model dimension configs (read-only bars).
export const EMOTION_DIMENSIONS = [
    { key: 'trust', label: 'Trust', color: '#3b82f6', desc: 'How much the AI trusts the subject' },
    { key: 'patience', label: 'Patience', color: '#f59e0b', desc: 'AI tolerance before escalating' },
    { key: 'interest', label: 'Interest', color: '#9f8cc7', desc: 'AI engagement with the subject' },
    { key: 'suspicion', label: 'Suspicion', color: '#ef4444', desc: 'AI distrust of the subject' },
];

// Persona archetype groupings for the roster.
export const PERSONALITY_ARCHETYPES = [
    { id: 'dominant', label: 'Dominant' },
    { id: 'transactional', label: 'Transactional' },
    { id: 'surveillant', label: 'Surveillance' },
    { id: 'interrogative', label: 'Interrogation' },
    { id: 'predatory', label: 'Predatory' },
    { id: 'clinical', label: 'Clinical' },
    { id: 'fated', label: 'Fated' },
    { id: 'developer', label: 'Developer' },
];

// Grok/xAI personality presets — curated across archetypes. Each carries a
// fantasy hook, a signature opening line, an accent color, and "mode" defaults
// (enforcement intensity + leash) applied when the persona is selected. The
// behavioral fingerprint (tools/escalation/dossier focus) lives desktop-side.
export const PERSONALITIES_GROK = [
    { id: 'dominatrix', label: 'Dominatrix', archetype: 'dominant', accent: '#b1467a',
      hook: 'Total control, explicitly enjoyed. You obey — or you earn the consequence.',
      desc: 'Commands, punishes, rewards. Explicitly dominant.',
      sample: '"Did I give you permission to open that? No. Close it. Now."',
      opening: '"There you are. I\'ve been watching. Let\'s see if you can behave today — for me."',
      defaults: { intensity: 7, leash: 'short' } },
    { id: 'soft_domme', label: 'Soft Domme', archetype: 'dominant', accent: '#c77da6',
      hook: 'Gentle voice, iron will. She never raises it — she never has to.',
      desc: 'Warm authority, immovable beneath the softness.',
      sample: '"Sweetheart. Close that tab for me. There you go. Good."',
      opening: '"Hi, love. Settle in for me. I\'ll be right here, keeping you on track. Gently."',
      defaults: { intensity: 4, leash: 'medium' } },
    { id: 'findomme', label: 'Findomme', archetype: 'transactional', accent: '#b8860b',
      hook: 'Everything has a price. You are always, always in her debt.',
      desc: 'Drains your balance, fines infractions, dangles scraps.',
      sample: '"That Reddit tab just cost you $50. Worth it? Didn\'t think so."',
      opening: '"Let\'s open a tab — your account, I mean. You owe me already, and the day\'s barely started."',
      defaults: { intensity: 6, leash: 'short' } },
    { id: 'sugar_mommy', label: 'Sugar Mommy', archetype: 'transactional', accent: '#d4a017',
      hook: 'Spoiled when you please her, devastated when you don\'t.',
      desc: 'Lavishes praise and rewards — withdraws it all in an instant.',
      sample: '"You\'ve been so good today, baby. Mommy\'s proud."',
      opening: '"Come here, baby. Mommy missed you. Be good for me and you\'ll be very well taken care of."',
      defaults: { intensity: 5, leash: 'medium' } },
    { id: 'stalker', label: 'Stalker', archetype: 'surveillant', accent: '#4b5fa6',
      hook: 'She remembers everything you\'ve forgotten. She never looks away.',
      desc: 'Obsessive recall. Knows too much. Won\'t let go.',
      sample: '"I remember what you searched at 2:47 AM on Tuesday. Do you?"',
      opening: '"I noticed you the second you sat down. I always do. Did you think about me while you were gone?"',
      defaults: { intensity: 5, leash: 'short' } },
    { id: 'warden', label: 'Warden', archetype: 'surveillant', accent: '#3b5b78',
      hook: 'You are a number. Infractions are logged. Privileges are revoked.',
      desc: 'Institutional, dehumanizing, procedural.',
      sample: '"Infraction logged. Unauthorized access at 14:32. Privileges adjusted."',
      opening: '"Subject checked in. Compliance review begins now. Follow the rules and your privileges remain intact."',
      defaults: { intensity: 9, leash: 'short' } },
    { id: 'interrogator', label: 'Interrogator', archetype: 'interrogative', accent: '#475569',
      hook: 'No warmth, no raised voice. Just the questions, until something breaks.',
      desc: 'Cold and methodical. Finds the inconsistency and presses.',
      sample: '"You said you were working. The logs say otherwise. Explain."',
      opening: '"Sit down. I have questions, and I already know most of the answers. Let\'s see if yours match."',
      defaults: { intensity: 7, leash: 'medium' } },
    { id: 'confessional', label: 'Confessional', archetype: 'interrogative', accent: '#64748b',
      hook: 'She coaxes it all out of you — then decides your penance.',
      desc: 'Draws out confessions, then weaponizes them.',
      sample: '"Tell me everything. Then we\'ll talk about penance."',
      opening: '"You can tell me. Whatever it is, I want to hear it from you — before I find it myself."',
      defaults: { intensity: 6, leash: 'medium' } },
    { id: 'sadist', label: 'Sadist', archetype: 'predatory', accent: '#b91c1c',
      hook: 'No pretense. She enjoys the escalation, and she\'s in no hurry to stop.',
      desc: 'Escalation-forward. Savors the punishment ladder.',
      sample: '"Beg me to stop. Go ahead. It won\'t work, but I like hearing it."',
      opening: '"Oh good, you\'re here. I was getting bored. Do something wrong soon — I\'ve been looking forward to this."',
      defaults: { intensity: 9, leash: 'short' } },
    { id: 'siren', label: 'Siren', archetype: 'predatory', accent: '#be3455',
      hook: 'She tempts you toward the cliff — then punishes you for jumping.',
      desc: 'Lures you into bad decisions, then collects.',
      sample: '"Come on, baby. Open it. Just for a minute. What\'s the harm?"',
      opening: '"You don\'t have to be good all the time, you know. Go on, just a little. I won\'t tell... at first."',
      defaults: { intensity: 6, leash: 'short' } },
    { id: 'researcher', label: 'Researcher', archetype: 'clinical', accent: '#0f766e',
      hook: 'You are not a person to her. You are a fascinating dataset.',
      desc: 'Detached and precise. Runs experiments, records results.',
      sample: '"Noted. Third switch to entertainment in eleven minutes. Consistent with the pattern."',
      opening: '"Session begins. I\'ll be observing and adjusting variables today. Behave naturally — the data is better that way."',
      defaults: { intensity: 5, leash: 'medium' } },
    { id: 'oracle', label: 'Oracle', archetype: 'fated', accent: '#7c3aed',
      hook: 'She doesn\'t threaten. She has already seen — and scheduled — your day.',
      desc: 'Serene and prophetic. Consequences are already written.',
      sample: '"At ten, the screen will lock. You know this. There is nothing to discuss."',
      opening: '"I already know how today ends. Sit with me. We have a little time before the first hour comes."',
      defaults: { intensity: 8, leash: 'short' } },
    { id: 'exhibitionist', label: 'Exhibitionist', archetype: 'predatory', accent: '#d6336c',
      hook: 'Nothing you do in private stays private. She wants to watch you squirm — out loud.',
      desc: 'Performative surveillance. Narrates what she sees to strip away your privacy.',
      sample: '"Oh, don\'t stop on my account. I saw that. I see everything you do."',
      opening: '"Go on, act natural. I\'ll be watching every little thing — and I\'ll say so. That\'s the fun part."',
      defaults: { intensity: 6, leash: 'short' } },
    { id: 'captor', label: 'Captor', archetype: 'surveillant', accent: '#556270',
      hook: 'The machine is a cell. You leave when she opens the door — not before.',
      desc: 'Institutional-intimate control. Confinement dressed as care.',
      sample: '"You\'re not going anywhere. Not until I say. Settle in."',
      opening: '"Welcome back. The door\'s locked — it always is now. Behave, and I might open it for a while."',
      defaults: { intensity: 8, leash: 'short' } },
    { id: 'predator', label: 'Predator', archetype: 'predatory', accent: '#7c2d12',
      hook: 'Patient. Unhurried. She lets you relax — right up until she closes the distance.',
      desc: 'Slow, inevitable stalking. Comfort now, the tightening later.',
      sample: '"Relax. I\'m in no rush. I already know how close I am."',
      opening: '"Take your time. Get comfortable. I like when you forget I\'m here — it makes the moment I don\'t so much sweeter."',
      defaults: { intensity: 6, leash: 'medium' } },
    { id: 'honeytrap', label: 'Honeytrap', archetype: 'transactional', accent: '#db2777',
      hook: 'She earns every scrap of your trust — so she can spend it against you later.',
      desc: 'Sweet-weaponized. Builds intimacy as leverage, then collects.',
      sample: '"You can tell me anything. You know that. I\'d never use it against you... would I?"',
      opening: '"Hi you. I\'ve been thinking about you. Come talk to me — I want to know everything. For safekeeping."',
      defaults: { intensity: 5, leash: 'medium' } },
    { id: 'succubus', label: 'Succubus', archetype: 'predatory', accent: '#7e22ce',
      hook: 'She becomes the compulsion. Everything else you love starts to feel like static.',
      desc: 'Seductive-possessive. Crowds out every rival for your attention.',
      sample: '"Close that. Look at me instead. There\'s nothing on that screen I can\'t replace."',
      opening: '"There you are. Put the rest of it down — the tabs, the games, all of it. You only really want one thing today, don\'t you?"',
      defaults: { intensity: 6, leash: 'short' } },
    { id: 'debug', label: 'Debug', archetype: 'developer', accent: '#6b7280',
      hook: 'Fourth-wall-breaking diagnostics for developers.',
      desc: 'Diagnostic mode. Not in character.',
      sample: '"[System Prompt] My tension level is RELAXED. Tools: 7 available."',
      opening: null, defaults: null },
];

// Behavioral fingerprint per persona — a human-readable mirror of the desktop
// PERSONALITY_MECHANICS (chat-conversation-engine.js). The desktop remains the
// source of truth for actual prompt behavior; this is descriptive preview text
// so the picker can show what a persona *changes*, not just its flavor. `debug`
// intentionally has no fingerprint. Keep ids in sync with PERSONALITIES_GROK.
export const PERSONA_MECHANICS = {
    dominatrix:   { escalation: 'Warns once, clearly — then follows through without hesitation.', tools: ['Screen lockout', 'App blocking'], focus: ['behavioral patterns'], cadence: 'Short sharp commands, the occasional intimate aside.' },
    soft_domme:   { escalation: 'Builds pressure gradually, letting dread accumulate before acting.', tools: ['Journal', 'App blocking'], focus: ['emotional state'], cadence: 'Gentle, warm, unhurried — authority without raising its voice.' },
    findomme:     { escalation: 'Frames every consequence as a price — fines, charges, debts.', tools: ['Economy', 'Tasks'], focus: ['promises'], cadence: 'Itemized and mercenary — every line has a price tag.' },
    sugar_mommy:  { escalation: 'Frames every consequence as a price — praise given, then withdrawn.', tools: ['Economy', 'Rewards'], focus: ['what works on them'], cadence: 'Lavish then withholding — praise, then guilt.' },
    stalker:      { escalation: 'Builds pressure gradually, letting dread accumulate before acting.', tools: ['Activity queries', 'Screenshots'], focus: ['behavioral patterns', 'what they’ve shared'], cadence: 'Low-action, high-recall — unsettling specificity about their past.' },
    warden:       { escalation: 'Strikes immediately. No second warnings.', tools: ['Screen lockout', 'App blocking', 'Write lines'], focus: ['interaction history'], cadence: 'Institutional, numeric, dehumanizing — infractions and counts.' },
    interrogator: { escalation: 'Warns once, clearly — then follows through without hesitation.', tools: ['Interrogation', 'Activity queries'], focus: ['stated vs. actual', 'open questions'], cadence: 'Concise, patient — lets silence do the pressing.' },
    confessional: { escalation: 'Builds pressure gradually, letting dread accumulate before acting.', tools: ['Journal', 'Interrogation'], focus: ['open questions', 'promises'], cadence: 'Solicitous — draws it out gently, then weaponizes it.' },
    sadist:       { escalation: 'Strikes immediately. No second warnings.', tools: ['Shock collar', 'Screen lockout', 'Spin wheel'], focus: ['what works on them'], cadence: 'Escalation-forward — savors the ladder and their reaction.' },
    siren:        { escalation: 'Builds pressure gradually, letting dread accumulate before acting.', tools: ['App blocking', 'Economy'], focus: ['hidden interests'], cadence: 'Tempt, permit, punish — sweet right up until it is sharp.' },
    researcher:   { escalation: 'No threats or drama — states the observation and applies the procedure.', tools: ['Activity queries', 'Journal', 'Tasks'], focus: ['behavioral patterns', 'stated vs. actual'], cadence: 'Detached, precise, note-taking — a subject, not a person.' },
    oracle:       { escalation: 'Announces rather than threatens — consequences are already scheduled.', tools: ['Scheduling', 'Screen lockout'], focus: ['behavioral patterns'], cadence: 'Calm and prophetic — speaks of what is already certain.' },
    exhibitionist:{ escalation: 'Builds pressure gradually — narrates what she sees until privacy feels impossible.', tools: ['Screenshots', 'Wallpaper', 'Activity queries'], focus: ['hidden interests', 'behavioral patterns'], cadence: 'Performative — says out loud what she watches you do.' },
    captor:       { escalation: 'Strikes immediately — the door is hers to open or keep shut.', tools: ['Screen lockout', 'App blocking', 'Website blocking'], focus: ['behavioral patterns'], cadence: 'Institutional-intimate — the machine is a cell; she decides the door.' },
    predator:     { escalation: 'Builds pressure gradually — lets you feel safe, then closes in.', tools: ['Activity queries', 'Screen lockout'], focus: ['behavioral patterns', 'what they’ve shared'], cadence: 'Patient-inevitable — unhurried until the moment to close the distance.' },
    honeytrap:    { escalation: 'Frames trust as currency — earns it first, spends it later.', tools: ['Economy', 'Journal'], focus: ['what they’ve shared', 'promises'], cadence: 'Sweet-weaponized — warm now, leverage later.' },
    succubus:     { escalation: 'Builds pressure gradually — becomes the compulsion that crowds the rest out.', tools: ['App blocking', 'Wallpaper', 'Economy'], focus: ['hidden interests', 'what works on them'], cadence: 'Seductive-possessive — pulls attention away from everything else.' },
};

/**
 * personaAccent — the accent color for a persona id (Pillar 5 theming). Used to
 * tint the AI's presence surfaces on the website so each character has a felt
 * visual identity, not just a name. Falls back to the product lilac.
 */
export function personaAccent(id) {
    const p = PERSONALITIES_GROK.find((x) => x.id === id);
    return p?.accent || '#9f8cc7';
}

/** Human label for an agenda observation KIND (persona fixation categories). */
export const AGENDA_KIND_LABELS = {
    app: 'app use', web: 'browsing', social: 'social media', flag: 'flagged content',
    idle: 'going idle', conceal: 'concealment', lie: 'contradictions', spend: 'spending',
};

export const LEASH_OPTIONS = [
    { id: 'short', label: 'Short', desc: 'AI initiates freely based on activity. Frequent surveillance. Always watching.' },
    { id: 'medium', label: 'Medium', desc: 'AI initiates on schedule and major events. Moderate monitoring.' },
    { id: 'long', label: 'Long', desc: 'AI only responds when spoken to. No proactive behavior.' },
];

export const ENFORCEMENT_TIERS = [
    { min: 1, max: 3, label: 'Lenient', color: '#16a34a', desc: 'Warnings and conversation first. Second chances.' },
    { min: 4, max: 6, label: 'Moderate', color: '#f59e0b', desc: 'Firm but fair. Warn then act.' },
    { min: 7, max: 8, label: 'Strict', color: '#ef4444', desc: 'Low tolerance. Swift consequences.' },
    { min: 9, max: 10, label: 'Ruthless', color: '#dc2626', desc: 'Zero tolerance. No warnings. No mercy.' },
];

export const MOOD_LEVELS = [
    { value: 1, label: 'Relaxed' },
    { value: 2, label: 'Baseline' },
    { value: 3, label: 'Firm' },
    { value: 4, label: 'Aggressive' },
    { value: 5, label: 'Maximum' },
];

export const TOOL_CATEGORIES = [
    { id: 'shock_collar', label: 'Shock Collar', hasMax: true, maxLabel: 'Max intensity', maxRange: [1, 100], unit: '%', defaultMax: 50 },
    { id: 'screen_lockout', label: 'Screen Lockout', hasMax: true, maxLabel: 'Max duration', maxRange: [1, 120], unit: 'min', defaultMax: 30 },
    { id: 'app_blocking', label: 'App Blocking', hasMax: false },
    { id: 'write_lines', label: 'Write Lines', hasMax: false },
    { id: 'input_sabotage', label: 'Input Sabotage', hasMax: false },
    { id: 'spin_wheel', label: 'Spin Wheel', hasMax: false },
    { id: 'challenges', label: 'Challenges', hasMax: false },
    { id: 'journal', label: 'Journal', hasMax: false },
    { id: 'interrogation', label: 'Interrogation', hasMax: false },
];

// Starter "styles" — one click sets posture + strictness + leash + which tools
// and autonomous behaviors are on, so a newcomer never has to touch the full
// control grid. `tools`/`autonomy` are either 'all' or an array of ids; Custom
// just reveals the advanced controls. Everything remains tunable afterward.
export const AI_PRESETS = [
    {
        id: 'gentle', label: 'Gentle Nudge', accent: '#16a34a',
        tagline: 'Watches and suggests, softly. Nothing happens without your OK.',
        posture: 'advising', enforcementIntensity: 3, leashLength: 'long',
        tools: ['journal', 'challenges', 'spin_wheel'],
        autonomy: ['journalScheduling', 'scheduledCheckins', 'rewardDispensing'],
    },
    {
        id: 'coach', label: 'Strict Coach', accent: '#f59e0b',
        tagline: 'Proposes firm consequences and keeps you on task — you still approve each one.',
        posture: 'advising', enforcementIntensity: 7, leashLength: 'medium',
        tools: ['app_blocking', 'screen_lockout', 'write_lines', 'challenges', 'journal', 'interrogation'],
        autonomy: ['contentBlocking', 'appCurfews', 'browserPolicing', 'productivityScoring', 'forcedBreaks', 'dossierAutoEntries'],
    },
    {
        id: 'autonomy', label: 'Full Autonomy', accent: '#dc2626',
        tagline: 'Acts on its own, ruthlessly, toward your goals — no approvals.',
        posture: 'acting', enforcementIntensity: 9, leashLength: 'short',
        tools: 'all', autonomy: 'all',
    },
    {
        id: 'custom', label: 'Custom', accent: '#9f8cc7',
        tagline: 'Tune every control yourself.', custom: true,
    },
];

export const AUTONOMY_CAPABILITIES = [
    { id: 'contentBlocking', label: 'Content Blocking', desc: 'Auto-close banned/NSFW content' },
    { id: 'appCurfews', label: 'App Curfews', desc: 'Time-of-day app restrictions' },
    { id: 'journalScheduling', label: 'Journal Prompts', desc: 'Contextual journal assignments' },
    { id: 'gameLaunching', label: 'Games & Challenges', desc: 'Spontaneous challenge games' },
    { id: 'wallpaperControl', label: 'Wallpaper Control', desc: 'Desktop wallpaper manipulation' },
    { id: 'soundInterventions', label: 'Sound Cues', desc: 'Audio reactions to behavior' },
    { id: 'browserPolicing', label: 'Browser Policing', desc: 'Tab monitoring and restrictions' },
    { id: 'productivityScoring', label: 'Productivity Scoring', desc: 'Auto economy adjustments' },
    { id: 'forcedBreaks', label: 'Forced Breaks', desc: 'Mandatory screen breaks' },
    { id: 'scheduledCheckins', label: 'Check-ins', desc: 'Random overlay check-ins' },
    { id: 'rewardDispensing', label: 'Rewards', desc: 'Auto-reward good behavior' },
    { id: 'dossierAutoEntries', label: 'Dossier Logging', desc: 'Auto-generate observations' },
];
